import React, { useState, useMemo, useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import Group from 'components/utils/Group'
import OfferDescriptionLoadingSkeleton from 'components/Common/Loading/OfferDescriptionLoadingSkeleton'

const DescriptionContainer = styled.div`
  padding: ${rem(8)} ${rem(12)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const DescriptionHeader = styled(Group)`
  margin-bottom: ${rem(4)};
  font-weight: bold;
  font-size: ${rem(14)};
  font-family: ${props => props.theme.font.primary.family};
`

const SparkleIcon = styled(SolidSparklesIcon)`
  margin-right: ${rem(8)};
  color: inherit;
`

const OfferDescriptionHeader = styled.span`
    color: ${props => props.theme.palette.highlight.secondary.normalForeground};
`

const StyledList = styled.ul`
  margin: 0;
  padding-left: ${rem(12)};
`

const ToggleHint = styled.span`
  margin-left: ${rem(4)};
  color: ${props => props.theme.palette.brand.primary.normal};
  text-decoration: underline;
`

const PlainText = styled.span`
  display: inline;
`

interface Props {
  description?: Array<string>
  isLoading?: boolean
  className?: string
  position?: number
  isError?: boolean
}

function OfferDescription({ description, isLoading = false, className, position }: Props) {
  const [isExpanded, setIsExpanded] = useState(false)
  const displayPosition = position !== undefined ? position + 1 : ''

  const lines = useMemo(() => description || [], [description])
  const hasMultipleLines = lines.length > 1

  const toggleExpanded = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
    setIsExpanded(previous => !previous)
  }, [])

  const bulletListItems = useMemo(
    () =>
      lines.map((line, index) => (
        <li key={`${line}-${index}`}>
          {line}
        </li>
      )),
    [lines],
  )

  const primaryLine = useMemo(() => lines[0] || '', [lines])

  const renderDescriptionContent = useMemo(() => {
    if (!hasMultipleLines) {
      return <PlainText>{primaryLine}</PlainText>
    }

    if (isExpanded) {
      return (
        <>
          <StyledList>{bulletListItems}</StyledList>
          <ToggleHint>Show less</ToggleHint>
        </>
      )
    }

    return (
      <>
        <PlainText>{primaryLine}</PlainText>
        <ToggleHint>Show more</ToggleHint>
      </>
    )
  }, [bulletListItems, hasMultipleLines, isExpanded, primaryLine])

  if (isLoading) {
    return <OfferDescriptionLoadingSkeleton position={position} className={className} />
  }

  if (!description || description.length === 0) {
    return null
  }

  return (
    <DescriptionContainer className={className}>
      <DescriptionHeader direction="horizontal" verticalAlign="center">
        <SparkleIcon size="S" />
        <OfferDescriptionHeader>
          {displayPosition === 1 ? 'Best match' : `Match #${displayPosition}`}
        </OfferDescriptionHeader>
      </DescriptionHeader>
      <BodyText
        variant="medium"
        colour="neutral-two"
        onClick={hasMultipleLines ? toggleExpanded : undefined}
      >
        {renderDescriptionContent}
      </BodyText>
    </DescriptionContainer>
  )
}

export default OfferDescription
